/**
 * JobTimeline - Timeline view of booking events
 * Updated for Booking & Jobs System Refactor
 *
 * Uses unified bookingEvents structure with proper field names.
 */

import {
  ArrowRight,
  MessageSquare,
  Camera,
  DollarSign,
  CheckCircle,
  Clock,
  UserCog,
  Bell,
  MailCheck,
  MailX,
  Eye,
  MousePointerClick,
  PenTool,
  ClipboardCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';
import { getStatusConfig } from './JobBoard';

/**
 * Timeline event data from API (bookingEvents table)
 */
export interface TimelineEventData {
  id: string;
  bookingId: string;
  eventType: 'STATUS_CHANGE' | 'NOTE' | 'PHOTO' | 'ESTIMATE' | 'APPROVAL' | 'ASSIGNMENT' | 'NOTIFICATION' | 'CHECKLIST';
  // New field names from bookingEvents
  previousStatus: string | null;
  newStatus: string | null;
  // Legacy field names (for backwards compat)
  previousState?: string | null;
  newState?: string | null;
  actorId: string | null;
  actorRole: 'staff' | 'customer' | 'system' | null;
  // New note fields
  publicNote: string | null;
  internalNote?: string | null;
  // Legacy note field
  notes?: string | null;
  metadata: Record<string, unknown> | null;
  isPublic?: boolean;
  createdAt: number;
}

/**
 * Event type configuration
 */
const EVENT_TYPE_CONFIG: Record<
  string,
  { label: string; icon: typeof ArrowRight; color: string; bgColor: string }
> = {
  STATUS_CHANGE: {
    label: 'Cambio de estado',
    icon: ArrowRight,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 border-blue-200',
  },
  NOTE: {
    label: 'Nota',
    icon: MessageSquare,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 border-gray-200',
  },
  PHOTO: {
    label: 'Foto',
    icon: Camera,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 border-purple-200',
  },
  ESTIMATE: {
    label: 'Presupuesto',
    icon: DollarSign,
    color: 'text-green-600',
    bgColor: 'bg-green-100 border-green-200',
  },
  APPROVAL: {
    label: 'Aprobación',
    icon: CheckCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100 border-amber-200',
  },
  ASSIGNMENT: {
    label: 'Asignación',
    icon: UserCog,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100 border-indigo-200',
  },
  NOTIFICATION: {
    label: 'Notificación',
    icon: Bell,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100 border-cyan-200',
  },
  CHECKLIST: {
    label: 'Checklist',
    icon: ClipboardCheck,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100 border-emerald-200',
  },
};

function formatRelativeTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;

  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFullDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface TimelineEventProps {
  event: TimelineEventData;
  isLast?: boolean;
}

function TimelineEvent({ event, isLast = false }: TimelineEventProps) {
  const eventConfig = EVENT_TYPE_CONFIG[event.eventType] || {
    label: event.eventType,
    icon: Clock,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100 border-gray-200',
  };
  const Icon = eventConfig.icon;

  // Get note (support both new and legacy field names)
  const note = event.publicNote || event.notes || event.internalNote;

  // Get status fields (support both new and legacy field names)
  const previousStatus = event.previousStatus || event.previousState;
  const newStatus = event.newStatus || event.newState;

  const renderEventContent = () => {
    switch (event.eventType) {
      case 'STATUS_CHANGE': {
        const prevConfig = previousStatus ? getStatusConfig(previousStatus) : null;
        const newConfig = newStatus ? getStatusConfig(newStatus) : null;

        return (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {prevConfig ? (
                <Badge
                  variant="outline"
                  className={cn('text-xs', prevConfig.bgColor, prevConfig.color)}
                >
                  {prevConfig.label}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Inicio</Badge>
              )}
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              {newConfig && (
                <Badge
                  variant="outline"
                  className={cn('text-xs', newConfig.bgColor, newConfig.color)}
                >
                  {newConfig.label}
                </Badge>
              )}
            </div>
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
          </div>
        );
      }

      case 'ESTIMATE': {
        const amount = event.metadata?.amount as number | undefined;
        return (
          <div className="space-y-1">
            {amount !== undefined && (
              <p className="text-base font-semibold text-green-700">
                {(amount / 100).toLocaleString('es-ES', {
                  style: 'currency',
                  currency: 'EUR',
                })}
              </p>
            )}
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
          </div>
        );
      }

      case 'APPROVAL': {
        const metaType = event.metadata?.type as string | undefined;
        if (metaType === 'signature_submitted') {
          const sigMessage = event.metadata?.message as string | undefined;
          return (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="text-xs bg-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" /> Firma recibida
                </Badge>
              </div>
              {sigMessage && (
                <div className="mt-1 rounded border-l-2 border-blue-500 bg-blue-50 px-3 py-1.5">
                  <p className="text-xs text-gray-600">Mensaje: <span className="font-medium text-gray-800">&quot;{sigMessage}&quot;</span></p>
                </div>
              )}
              {note && <p className="text-sm text-muted-foreground">{note}</p>}
            </div>
          );
        }
        const approved = event.metadata?.approved as boolean | undefined;
        const approvedBy = event.metadata?.approvedBy as string | undefined;
        return (
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={approved ? 'default' : 'destructive'}
              className={cn('text-xs', approved ? 'bg-green-600' : '')}
            >
              {approved ? (
                <><CheckCircle className="h-3 w-3 mr-1" /> Aprobado</>
              ) : (
                'Rechazado'
              )}
            </Badge>
            {approvedBy && (
              <span className="text-xs text-muted-foreground">por {approvedBy}</span>
            )}
          </div>
        );
      }

      case 'ASSIGNMENT': {
        const bayId = event.metadata?.bayId as string | undefined;
        const staffId = event.metadata?.staffId as string | undefined;
        return (
          <div className="space-y-1">
            {bayId !== undefined && (
              <p className="text-sm">
                <span className="text-muted-foreground">Bahía:</span>{' '}
                <span className="font-medium">{bayId || 'Sin asignar'}</span>
              </p>
            )}
            {staffId !== undefined && (
              <p className="text-sm">
                <span className="text-muted-foreground">Técnico:</span>{' '}
                <span className="font-medium">{staffId || 'Sin asignar'}</span>
              </p>
            )}
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
          </div>
        );
      }

      case 'NOTE':
        return note ? <p className="text-sm">{note}</p> : null;

      case 'PHOTO':
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Camera className="h-4 w-4" />
            <span>{note || 'Foto añadida'}</span>
          </div>
        );

      case 'CHECKLIST': {
        const action = event.metadata?.action as string | undefined;
        const itemLabel = event.metadata?.itemLabel as string | undefined;
        const value = event.metadata?.value;

        return (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {action === 'attachment_uploaded'
                ? `Archivo subido${itemLabel ? ` · ${itemLabel}` : ''}`
                : `Valor actualizado${itemLabel ? ` · ${itemLabel}` : ''}`}
            </p>
            {value !== undefined && value !== null && (
              <p className="text-xs text-muted-foreground">Valor: {typeof value === 'string' ? value : JSON.stringify(value)}</p>
            )}
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
          </div>
        );
      }

      case 'NOTIFICATION': {
        const metaType = event.metadata?.type as string | undefined;
        if (metaType === 'signature_request') {
          const sigMessage = event.metadata?.message as string | undefined;
          return (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  <PenTool className="h-3 w-3 mr-1 text-blue-600" /> Firma solicitada
                </Badge>
              </div>
              {sigMessage && (
                <div className="mt-1 rounded border-l-2 border-blue-500 bg-blue-50 px-3 py-1.5">
                  <p className="text-xs text-gray-600">Mensaje: <span className="font-medium text-gray-800">&quot;{sigMessage}&quot;</span></p>
                </div>
              )}
              {note && <p className="text-sm text-muted-foreground">{note}</p>}
            </div>
          );
        }
        const status = event.metadata?.status as string | undefined;
        const statusBadge = status
          ? (
            <Badge variant="outline" className="text-xs">
              {status === 'delivered' && <><MailCheck className="h-3 w-3 mr-1 text-emerald-600" />Entregado</>}
              {status === 'opened' && <><Eye className="h-3 w-3 mr-1 text-blue-600" />Abierto</>}
              {status === 'clicked' && <><MousePointerClick className="h-3 w-3 mr-1 text-indigo-600" />Clic</>}
              {status === 'bounced' && <><MailX className="h-3 w-3 mr-1 text-red-600" />Rebotado</>}
            </Badge>
          )
          : null;

        return (
          <div className="space-y-1">
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
            {statusBadge}
          </div>
        );
      }

      default:
        return note ? <p className="text-sm">{note}</p> : null;
    }
  };

  return (
    <div className="relative flex gap-3 pl-1">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-4 top-8 bottom-0 w-px bg-border" />
      )}

      {/* Icon */}
      <div
        className={cn(
          'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border',
          eventConfig.bgColor
        )}
      >
        <Icon className={cn('h-4 w-4', eventConfig.color)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-4">
        {/* Hide label for STATUS_CHANGE as it's implicit from the status badges */}
        {event.eventType !== 'STATUS_CHANGE' && (
          <p className={cn('text-sm font-medium mb-1', eventConfig.color)}>
            {eventConfig.label}
          </p>
        )}
        <div>{renderEventContent()}</div>
        <p
          className="mt-2 text-xs text-muted-foreground"
          title={formatFullDate(event.createdAt)}
        >
          {formatRelativeTime(event.createdAt)}
        </p>
      </div>
    </div>
  );
}

interface JobTimelineProps {
  events: TimelineEventData[];
  loading?: boolean;
}

export function JobTimeline({ events, loading = false }: JobTimelineProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3 pl-1">
                <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-2 pb-4">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-6 w-2/3" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Clock className="mx-auto h-12 w-12 text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">Sin eventos en la línea de tiempo</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="relative">
          <div className="space-y-0">
            {events.map((event, index) => (
              <TimelineEvent
                key={event.id}
                event={event}
                isLast={index === events.length - 1}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
